'use client';

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/utils';

export interface ConfirmDialogContentProps {
    cancelText?: string;
    checkboxLabel?: ReactNode;
    children?: ReactNode;
    confirmText?: string;
    confirmTone?: 'primary' | 'danger';
    defaultChecked?: boolean;
    description: ReactNode;
    title: ReactNode;
}

export interface ConfirmDialogProps extends ConfirmDialogContentProps {
    checked?: boolean;
    onCancel?: () => void;
    onCheckedChange?: (checked: boolean) => void;
    onConfirm: (checked: boolean) => void | Promise<void>;
    onOpenChange: (open: boolean) => void;
    open: boolean;
    className?: string;
    overlayClassName?: string;
    showCancel?: boolean;
}

export function ConfirmDialog({
    cancelText = '取消',
    checked,
    checkboxLabel,
    children,
    confirmText = '确认',
    confirmTone = 'primary',
    defaultChecked = false,
    description,
    onCancel,
    onCheckedChange,
    onConfirm,
    onOpenChange,
    open,
    title,
    className,
    overlayClassName,
    showCancel = true,
}: ConfirmDialogProps) {
    const isControlled = checked !== undefined;
    const [internalChecked, setInternalChecked] = useState(defaultChecked);
    const cancelButtonRef = useRef<HTMLButtonElement>(null);
    const confirmButtonRef = useRef<HTMLButtonElement>(null);
    const resolvedChecked = isControlled ? checked : internalChecked;

    useEffect(() => {
        if (open && !isControlled) {
            setInternalChecked(defaultChecked);
        }
    }, [defaultChecked, isControlled, open]);

    const handleCheckedChange = (nextChecked: boolean) => {
        if (!isControlled) {
            setInternalChecked(nextChecked);
        }
        onCheckedChange?.(nextChecked);
    };

    const handleCancel = () => {
        onCancel?.();
        onOpenChange(false);
    };

    const handleConfirm = async () => {
        try {
            await onConfirm(Boolean(resolvedChecked));
            onOpenChange(false);
        } catch {
            // 保持弹窗打开，让调用方决定是否展示错误和如何重试。
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!showCancel || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
            return;
        }

        const activeElement = document.activeElement;
        const isActionFocused = activeElement === cancelButtonRef.current || activeElement === confirmButtonRef.current;

        if (!isActionFocused) {
            return;
        }

        event.preventDefault();

        if (event.key === 'ArrowLeft') {
            cancelButtonRef.current?.focus();
        } else {
            confirmButtonRef.current?.focus();
        }
    };

    return (
        <Dialog onOpenChange={onOpenChange} open={open}>
            <DialogContent
                className={cn("!z-[999999] max-w-[340px] gap-0 rounded-[16px] border border-[#494949] bg-[#292929] p-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.6)] sm:rounded-[16px] [&>button]:hidden", className)}
                onKeyDown={handleKeyDown}
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    confirmButtonRef.current?.focus();
                }}
                overlayClassName={cn("!z-[999999]", overlayClassName)}
            >
                <div className="flex flex-col">
                    <DialogTitle asChild>
                        <div className="text-center text-[13px] font-medium text-[#EBEBEB]">
                            {title}
                        </div>
                    </DialogTitle>
                    
                    <div className="my-4 h-px w-full bg-[#000000]" />
                    
                    <DialogDescription asChild>
                        <div className="text-center text-[13px] leading-relaxed text-[#D0D0D0] mb-6 whitespace-pre-line">
                            {description}
                        </div>
                    </DialogDescription>

                    {children}

                    {checkboxLabel ? (
                        <label className="mb-6 flex items-start gap-3 self-center rounded-[12px] border border-white/10 bg-white/5 px-4 py-3 text-[13px] leading-6 text-white/80">
                            <input
                                checked={Boolean(resolvedChecked)}
                                className="mt-0.5 h-4 w-4 rounded border-white/24 bg-white/10 text-[#2f8af7] accent-[#2f8af7]"
                                onChange={(event) => handleCheckedChange(event.currentTarget.checked)}
                                type="checkbox"
                            />
                            <span>{checkboxLabel}</span>
                        </label>
                    ) : null}

                    <div className="flex items-center justify-center gap-3 w-full">
                        {showCancel && <Button
                            ref={cancelButtonRef}
                            className="flex-1 h-[32px] rounded-full border-0 bg-[#383838] px-5 text-[14px] font-medium text-[#EBEBEB] hover:bg-[#484848]"
                            onClick={handleCancel}
                            variant="secondary"
                        >
                            {cancelText}
                        </Button>}
                        <Button
                            ref={confirmButtonRef}
                            className={cn(
                                'flex-1 h-[32px] rounded-full border-0 px-5 text-[14px] font-medium text-white shadow-none',
                                confirmTone === 'danger'
                                    ? 'bg-[#2D8CEB] hover:bg-[#409DFD]'
                                    : 'bg-[#2D8CEB] hover:bg-[#409DFD]',
                            )}
                            onClick={() => {
                                void handleConfirm();
                            }}
                        >
                            {confirmText}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
